var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { logBadgeHistoryActionFunctionDef, printAccountBadgesBulkActionName, } from '../../../../backend/features/badges/shared';
import { useDownloadAccountBadgeAttachments } from '../../../../ui/features/badge-management/hooks/useDownloadAccountBadgeAttachments';
import { useAccountBadgeStorage } from '../../../../ui/features/form/hooks/useAccountBadgeStorage';
import { useBadgingTranslation } from '../../../../ui/hooks';
import { getFilterForBulkAction } from '../../../../ui/util/getFilterForBulkAction';
import { showFormDialog } from '@juvare/common-form-ui/showFormDialog';
import { showConfirmDialog, showErrorDialog, showProgressDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { handleErrorSnackbar, showAlertSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { launchBulkAction } from '@juvare/platform-sdk-react/ui/features/bulk-actions';
import { useTypedPlatformFunction } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import { Typography } from '@mui/material';
import React from 'react';
export function useAccountBadgePrintBulkAction({ badgeId, accountBadgeSelectedIds, onClose, appliedStorageFilters, filteredRowCount, }) {
    const { t } = useBadgingTranslation();
    const bulkActionFilters = getFilterForBulkAction(accountBadgeSelectedIds, appliedStorageFilters);
    const accountBadgeStorage = useAccountBadgeStorage();
    const logBadgeHistoryAction = useTypedPlatformFunction(logBadgeHistoryActionFunctionDef);
    const { downloadAccountBadgeAttachmentsZip } = useDownloadAccountBadgeAttachments();
    const fetchAccountBadgesByPrintRequestParams = (requests) => __awaiter(this, void 0, void 0, function* () {
        return yield accountBadgeStorage.listColumns({
            filter: [
                {
                    op: 'or',
                    filter: requests.map(({ requestId, accountBadgeId }) => ({
                        op: 'and',
                        filter: [
                            {
                                column: 'printRequestId',
                                value: requestId,
                                op: '=',
                            },
                            {
                                column: 'printStatus',
                                value: ['failed', 'success'],
                                op: 'in',
                            },
                            {
                                id: accountBadgeId,
                                op: '=',
                            },
                        ],
                    })),
                },
            ],
        }, 'printStatus', 'printRequestId', 'accountDisplayName');
    });
    const pollUntilAllPrintAccountBadgeRequestsFinished = (requests) => __awaiter(this, void 0, void 0, function* () {
        let response = yield fetchAccountBadgesByPrintRequestParams(requests);
        while (response.total !== requests.length) {
            yield new Promise((resolve) => {
                setTimeout(resolve, 1000);
            });
            response = yield fetchAccountBadgesByPrintRequestParams(requests);
        }
        return response;
    });
    const processPrintRequests = (result, done) => {
        pollUntilAllPrintAccountBadgeRequestsFinished(result.requests)
            .then(({ rows }) => {
            try {
                done();
                const failedRequestsCount = rows.filter(({ columns }) => columns.printStatus === 'failed').length;
                if (failedRequestsCount) {
                    handleErrorSnackbar(t('badges.badgeDetails.accountBadgeTable.bulkActions.printAccountBadge.requestFailed', {
                        count: failedRequestsCount,
                    }));
                }
                const successfullyPrintedAccountBadges = rows.filter(({ columns }) => columns.printStatus === 'success');
                if (successfullyPrintedAccountBadges.length) {
                    logBadgeHistoryAction({
                        accountBadgeIds: successfullyPrintedAccountBadges.map(({ id }) => id),
                        badgeId: badgeId,
                        action: 'accountBadgeDistributionStatusPrinted',
                    }).catch(handleErrorSnackbar);
                    showConfirmDialog({
                        title: t('badges.print.confirmDownloadPdfReport'),
                        confirmLabel: t('common.download'),
                        onCancel: onClose,
                        onConfirm: () => {
                            downloadAccountBadgeAttachmentsZip(successfullyPrintedAccountBadges.map(({ id, columns }) => ({
                                accountBadgeId: id,
                                accountDisplayName: columns.accountDisplayName,
                            }))).catch(handleErrorSnackbar);
                            onClose();
                        },
                    });
                }
                else {
                    onClose();
                }
            }
            catch (error) {
                done();
                onClose();
                handleErrorSnackbar(error);
            }
        })
            .catch((error) => {
            done();
            onClose();
            handleErrorSnackbar(error);
        });
    };
    const handlePrintAccountBadges = () => {
        if (window.location.hostname === 'localhost') {
            return showErrorDialog({
                title: t('badges.print.pdfGenerationNotSupportedLocalhost'),
            });
        }
        showFormDialog({
            title: t('badges.badgeDetails.accountBadgeTable.bulkActions.printAccountBadge.title', {
                count: accountBadgeSelectedIds.length > 0 ? accountBadgeSelectedIds.length : filteredRowCount,
            }),
            config: {
                type: 'form',
                children: [
                    {
                        type: 'form.content',
                        key: 'content',
                        render: () => (React.createElement(Typography, { variant: 'body1', color: themeColors.text.subtle }, t('badges.badgeDetails.accountBadgeTable.bulkActions.printAccountBadge.description'))),
                    },
                ],
            },
            submitLabel: t('common.save'),
            onSubmit: () => {
                launchBulkAction({
                    title: t('badges.badgeDetails.accountBadgeTable.bulkActions.printAccountBadge.progressMessage'),
                    type: printAccountBadgesBulkActionName,
                    params: { badgeId, filter: bulkActionFilters },
                    runSync: false,
                    onClose: ({ status, result }) => {
                        if (status === 'finished') {
                            showAlertSnackbar({
                                horizontalPosition: 'center',
                                type: (result === null || result === void 0 ? void 0 : result.requests.length) ? 'success' : 'error',
                                verticalPosition: 'bottom',
                                autoHideDuration: 2000,
                                message: (result === null || result === void 0 ? void 0 : result.requests.length)
                                    ? t('badges.badgeDetails.accountBadgeTable.bulkActions.printAccountBadge.successMessage', {
                                        count: result.requests.length,
                                    })
                                    : t('badges.badgeDetails.accountBadgeTable.bulkActions.invalidMessage', {
                                        action: 'Print',
                                    }),
                            });
                            if (result === null || result === void 0 ? void 0 : result.requests.length) {
                                showProgressDialog({
                                    title: t('badges.print.generatingPdfReport'),
                                    callback: (done) => processPrintRequests(result, done),
                                });
                            }
                            else {
                                onClose();
                            }
                        }
                        else if (status === 'failed') {
                            showErrorDialog({
                                title: t('badges.badgeDetails.accountBadgeTable.bulkActions.printAccountBadge.failedMessage'),
                            });
                            onClose();
                        }
                    },
                });
            },
            sx: {
                pt: 0,
            },
        });
    };
    return {
        handlePrintAccountBadges,
    };
}
